import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { LanguageSwitcher } from '../common/LanguageSwitcher';
import { AccountMenu } from './AccountMenu';
import { ROUTES } from '../../utils/constants';

export function Header() {
  const { t } = useTranslation(['common', 'dashboard']);
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const close = () => setMenuOpen(false);

  // Used by the mobile menu's inline logout row only — the desktop avatar
  // dropdown's logout lives in AccountMenu.
  async function handleLogout() {
    close();
    await logout();
    navigate(ROUTES.LOGIN);
  }

  return (
    <header className="bg-white/95 backdrop-blur-sm border-b border-neutral-200/60 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        {/* Logo */}
        <Link to={ROUTES.HOME} className="flex items-center gap-2 group" onClick={close}>
          <img src="/logo.png" alt="werk42" className="w-7 h-7" />
          <span className="text-lg font-semibold text-neutral-900 group-hover:text-primary-700 transition-colors">
            {t('common:appName')}
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6">
          {isAuthenticated ? (
            <>
              <Link to={ROUTES.DASHBOARD} className="text-sm font-medium text-neutral-600 hover:text-accent-600 transition-colors">
                {t('dashboard:title')}
              </Link>
              <LanguageSwitcher />
              <AccountMenu />
            </>
          ) : (
            <>
              <Link to={ROUTES.PRICING} className="text-sm font-medium text-neutral-600 hover:text-neutral-900 transition-colors">
                Pricing
              </Link>
              <LanguageSwitcher />
              <Link to={ROUTES.LOGIN} className="text-sm font-medium text-neutral-600 hover:text-neutral-900 transition-colors">
                {t('common:login')}
              </Link>
            </>
          )}
        </nav>

        {/* Mobile hamburger */}
        <button
          className="md:hidden flex flex-col justify-center items-center w-8 h-8 gap-1.5 bg-transparent border-none outline-none cursor-pointer"
          onClick={() => setMenuOpen(o => !o)}
          aria-label="Toggle menu"
        >
          <span className={`block h-0.5 w-5 bg-neutral-700 transition-all duration-200 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
          <span className={`block h-0.5 w-5 bg-neutral-700 transition-all duration-200 ${menuOpen ? 'opacity-0' : ''}`} />
          <span className={`block h-0.5 w-5 bg-neutral-700 transition-all duration-200 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden border-t border-neutral-200/60 bg-white px-6 py-4 flex flex-col gap-4">
          {isAuthenticated ? (
            <>
              <Link to={ROUTES.DASHBOARD} onClick={close} className="text-sm font-medium text-neutral-700 hover:text-neutral-900 transition-colors">
                {t('dashboard:title')}
              </Link>
              <Link to={ROUTES.SETTINGS} onClick={close} className="text-sm font-medium text-neutral-700 hover:text-neutral-900 transition-colors">
                {t('common:settings')}
              </Link>
              <div className="pt-2 border-t border-neutral-100 flex items-center justify-between">
                <span className="text-sm text-neutral-500">{user?.email}</span>
                <button
                  onClick={handleLogout}
                  className="text-sm text-neutral-600 hover:text-neutral-900"
                >
                  {t('common:logout')}
                </button>
              </div>
              <LanguageSwitcher />
            </>
          ) : (
            <>
              <Link to={ROUTES.PRICING} onClick={close} className="text-sm font-medium text-neutral-700 hover:text-neutral-900 transition-colors">
                Pricing
              </Link>
              <LanguageSwitcher />
              <Link to={ROUTES.LOGIN} onClick={close} className="text-sm font-medium text-neutral-700 hover:text-neutral-900 transition-colors">
                {t('common:login')}
              </Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
